import { InputContentPart } from '@ag-ui/core';
import { isEmpty, isNil, isString } from 'lodash';
import { AskPromptAttachment } from '../api';
import { AskAttachment, AskAttachmentSummary } from '../types';
import { isAskAudio } from './askAttachmentTypes';

/**
 * Attachments as AG-UI content parts, which is where they live once a question has been asked.
 *
 * AG-UI already models a multimodal user turn: `UserMessage.content` is `string |
 * InputContentPart[]`, and image, audio and document parts all take a URL source. Putting
 * attachments there rather than in a map beside the transcript means the transcript stays the
 * only record of what a question carried — so rendering it, persisting it (`askSession` stores
 * raw AG-UI messages) and rebuilding the request after an abort all work off one source, and
 * cannot disagree.
 *
 * Only URL sources are ever produced. A `data` source would put the whole file, base64-encoded,
 * into a transcript that gets written to `localStorage` under a 256k budget — one photo would
 * exceed it, and an over-budget write is discarded rather than truncated, so the conversation
 * would silently vanish.
 */

/**
 * Where the filename lives: a part has a source and a type, but no name of its own.
 *
 * `key` is here for the same reason — the part's `source` holds the public URL, which is what
 * renders, while the gateway addresses the same file by its S3 key. A part written before `key`
 * existed has none, which is why it is optional and why `toAskPromptAttachments` skips those.
 */
interface AskPartMetadata {
  name: string;
  key?: string;
  durationMs?: number;
}

function partMetadata(metadata: unknown): AskPartMetadata | undefined {
  if (isNil(metadata) || typeof metadata !== 'object') {
    return undefined;
  }
  const { name, key, durationMs } = metadata as Partial<AskPartMetadata>;
  if (!isString(name) || isEmpty(name)) {
    return undefined;
  }
  return {
    name,
    ...(isString(key) && !isEmpty(key) ? { key } : {}),
    ...(typeof durationMs === 'number' ? { durationMs } : {}),
  };
}

/** Which kind of part carries this file, which is how the agent knows how to read it. */
function partType(attachment: AskAttachmentSummary): 'image' | 'audio' | 'document' {
  if (isAskAudio(attachment)) {
    return 'audio';
  }
  if (attachment.contentType.startsWith('image/')) {
    return 'image';
  }
  // Everything else Ask accepts — pdf, text, markdown, csv, docx, xlsx — is a document.
  return 'document';
}

/**
 * Content parts for the attachments a question is being sent with.
 *
 * Only `ready` attachments are included. An attachment still uploading has a `blob:` URL, which
 * means nothing outside this browser tab; sending one would give the agent a URL it cannot
 * fetch, and it would be stored in the transcript as though it were real.
 */
export function toAttachmentParts(attachments: AskAttachment[]): InputContentPart[] {
  return attachments
    .filter(attachment => attachment.status === 'ready')
    .map(attachment => ({
      type: partType(attachment),
      source: {
        type: 'url' as const,
        value: attachment.url,
        mimeType: attachment.contentType,
      },
      metadata: {
        name: attachment.name,
        ...(isEmpty(attachment.key) ? {} : { key: attachment.key }),
        ...(isNil(attachment.durationMs) ? {} : { durationMs: attachment.durationMs }),
      },
    }));
}

/**
 * The content for a user message: a plain string when there are no attachments, so a text-only
 * question is stored exactly as it always was, and parts only when there is something to carry.
 */
export function toUserMessageContent(
  prompt: string,
  attachments: AskAttachment[],
): string | InputContentPart[] {
  const parts = toAttachmentParts(attachments);
  if (isEmpty(parts)) {
    return prompt;
  }
  return [{ type: 'text', text: prompt }, ...parts];
}

/** The attachment parts of a user turn, or none when it was a plain question. */
export function attachmentPartsOf(content: string | InputContentPart[]): InputContentPart[] {
  if (isString(content)) {
    return [];
  }
  return content.filter(part => part.type !== 'text');
}

/**
 * What a turn's attachments were, for rendering it. Parts that carry no usable URL or lost
 * their filename are skipped rather than shown as a blank row.
 */
export function toAskAttachmentSummaries(
  content: string | InputContentPart[],
): AskAttachmentSummary[] {
  return attachmentPartsOf(content).flatMap(part => {
    if (!('source' in part) || part.source.type !== 'url' || isEmpty(part.source.value)) {
      return [];
    }
    const metadata = partMetadata(part.metadata);
    if (isNil(metadata)) {
      return [];
    }
    return [
      {
        name: metadata.name,
        contentType: part.source.mimeType ?? '',
        url: part.source.value,
        ...(isEmpty(metadata.key) ? {} : { key: metadata.key }),
        ...(isNil(metadata.durationMs) ? {} : { durationMs: metadata.durationMs }),
      },
    ];
  });
}

/**
 * A turn's attachments as the gateway needs them. Derived from the transcript rather than passed
 * alongside it, so a retried or resumed run sends the files the question actually carries.
 *
 * A file with no `key` is skipped rather than sent: the gateway addresses attachments by key alone,
 * so there is nothing to send for it. That is only reachable for a conversation restored from
 * `localStorage` that was stored before keys were kept — the file is still shown in the transcript,
 * it just cannot be handed to the agent again on a retry.
 */
export function toAskPromptAttachments(
  content: string | InputContentPart[],
): AskPromptAttachment[] {
  return toAskAttachmentSummaries(content).flatMap(({ key, name, contentType }) =>
    isEmpty(key) ? [] : [{ key: key as string, mimeType: contentType, fileName: name }],
  );
}
